import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, Youtube, Instagram, Facebook, Linkedin } from 'lucide-react';

const SideMenu = () => {
  const whatsappUrl = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá estou no site e preciso de ajuda!');
  const linkedinUrl = 'https://www.linkedin.com/in/fernando-tech-b78b40322';
  const instagramUrl = 'https://www.instagram.com/fernandotechrp/';
  const facebookUrl = 'https://www.facebook.com/fernandotechrp';
  const tiktokUrl = 'https://www.tiktok.com/@fernandotechrp';
  const youtubeUrl = 'https://www.youtube.com/@fernandotechrp';

  // Esconde no mobile ao rolar, mostra ao parar
  const [visible, setVisible] = useState(true);
  const scrollTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      // Só aplica o comportamento em mobile (< 768px)
      if (window.innerWidth >= 768) return;
      setVisible(false);
      clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setVisible(true), 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimer.current);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const TikTokIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  const btnBase = "group relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg";
  const tooltipBase = "hidden lg:block absolute right-16 top-1/2 -translate-y-1/2 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none shadow-xl border border-slate-700";

  return (
    <div
      className="fixed right-2 sm:right-4 md:right-6 bottom-4 sm:bottom-6 z-40 flex flex-col gap-2 sm:gap-3"
      style={{
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(110%)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className="bg-slate-900/90 backdrop-blur-md rounded-xl sm:rounded-2xl p-1.5 sm:p-2 shadow-2xl border border-slate-700/50 flex flex-col gap-1.5 sm:gap-2">

        {/* 1 - Voltar ao Topo */}
        <button
          onClick={scrollToTop}
          className={`${btnBase} bg-gradient-to-br from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 shadow-blue-500/30`}
          aria-label="Voltar ao topo"
          data-testid="scroll-to-top-btn"
        >
          <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
          <span className={tooltipBase}>Voltar ao Topo</span>
        </button>

        <div className="w-full h-px bg-slate-700/50"></div>

        {/* 2 - LinkedIn */}
        <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
          className={`${btnBase} bg-gradient-to-br from-[#0077B5] to-[#005582] hover:from-[#0088cc] hover:to-[#0066aa] shadow-[#0077B5]/30`}
          aria-label="LinkedIn" data-testid="linkedin-btn"
        >
          <Linkedin className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
          <span className={tooltipBase}>Conecte no LinkedIn</span>
        </a>

        {/* 3 - Instagram */}
        <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
          className={`${btnBase} bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-500 hover:via-pink-400 hover:to-orange-300 shadow-pink-500/30`}
          aria-label="Instagram" data-testid="instagram-btn"
        >
          <Instagram className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
          <span className={tooltipBase}>Siga no Instagram</span>
        </a>

        {/* 4 - Facebook */}
        <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
          className={`${btnBase} bg-gradient-to-br from-blue-700 to-blue-500 hover:from-blue-600 hover:to-blue-400 shadow-blue-600/30`}
          aria-label="Facebook" data-testid="facebook-btn"
        >
          <Facebook className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
          <span className={tooltipBase}>Siga no Facebook</span>
        </a>

        {/* 5 - YouTube */}
        <a href={youtubeUrl} target="_blank" rel="noopener noreferrer"
          className={`${btnBase} bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 shadow-red-500/30`}
          aria-label="YouTube" data-testid="youtube-btn"
        >
          <Youtube className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
          <span className={tooltipBase}>Inscreva-se no YouTube</span>
        </a>

        {/* 6 - TikTok */}
        <a href={tiktokUrl} target="_blank" rel="noopener noreferrer"
          className={`${btnBase} bg-black hover:bg-gradient-to-br hover:from-[#ff0050] hover:via-black hover:to-[#00f2ea] border border-slate-600 hover:border-transparent overflow-hidden`}
          aria-label="TikTok" data-testid="tiktok-btn"
        >
          <span className="text-white relative z-10"><TikTokIcon /></span>
          <span className={tooltipBase}>Siga no TikTok</span>
        </a>

        {/* 7 - WhatsApp */}
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
          className="group relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg shadow-green-500/30 overflow-hidden"
          aria-label="WhatsApp" data-testid="whatsapp-btn"
        >
          <img src="./whatsapp-business-circle.png" alt="WhatsApp" className="w-full h-full object-cover" />
          <span className={tooltipBase}>Fale no WhatsApp</span>
        </a>
      </div>
    </div>
  );
};

export default SideMenu;
